// Admin impersonation ("login as user"), plan: cbexchange
// docs/ADMIN_IMPERSONATION_PLAN.md.
//
// While an admin acts as another user, the user's session is the normal
// `token` / `rights`, and the admin's own login waits under IMPERSONATION_KEY.
// Every switch ends with a full page load: React Query, the WebSocket, the
// request cache and every module-level cache start empty, so nothing of one
// account can show up in the other.

const IMPERSONATION_KEY = 'impersonation';

// Per-account view state that must not leak between the two accounts.
const USER_SCOPED_KEYS = ['apikey', 'terminal_last_path', 'terminal_account_types'];

const API_URL = process.env.REACT_APP_API_URI;

export function getImpersonation() {
  try {
    const raw = localStorage.getItem(IMPERSONATION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

export function isImpersonating() {
  return !!getImpersonation()?.adminToken;
}

function readKeys(keys) {
  const out = {};
  keys.forEach((key) => {
    const value = localStorage.getItem(key);
    if (value !== null) out[key] = value;
  });
  return out;
}

function writeKeys(keys, values) {
  keys.forEach((key) => {
    if (values && values[key] !== undefined) localStorage.setItem(key, values[key]);
    else localStorage.removeItem(key);
  });
}

// `data` is the answer of POST /admin/impersonate.
export function startImpersonation(data, landing = '/dashboard') {
  if (isImpersonating()) return;
  localStorage.setItem(IMPERSONATION_KEY, JSON.stringify({
    adminToken: localStorage.getItem('token'),
    adminRights: localStorage.getItem('rights'),
    adminScoped: readKeys(USER_SCOPED_KEYS),
    user: data.impersonatedUser,
    admin: data.impersonator,
    expiresAt: data.expiresAt,
  }));
  writeKeys(USER_SCOPED_KEYS, {});
  localStorage.setItem('token', data.token);
  localStorage.setItem('rights', JSON.stringify(data.rights || []));
  window.location.assign(landing);
}

// Put the admin's own login back and reload. Does not talk to the server.
function restoreAdmin(landing) {
  const state = getImpersonation();
  localStorage.removeItem(IMPERSONATION_KEY);
  if (!state?.adminToken) return false;
  localStorage.setItem('token', state.adminToken);
  if (state.adminRights !== null && state.adminRights !== undefined) {
    localStorage.setItem('rights', state.adminRights);
  } else {
    localStorage.removeItem('rights');
  }
  writeKeys(USER_SCOPED_KEYS, state.adminScoped);
  window.location.assign(landing);
  return true;
}

// End the impersonation on the server (best effort) and return to the admin.
export async function stopImpersonation(landing = '/admin/impersonate') {
  const token = localStorage.getItem('token');
  try {
    await fetch(`${API_URL}/admin/impersonate/stop`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    });
  } catch (e) {
    // The session dies on its own after 2 h; the admin goes back regardless.
  }
  restoreAdmin(landing);
}

// Called where a request or the WebSocket finds the session gone (401/4001).
// While impersonating that means the 2 h are over: back to the admin's own
// login instead of the login page. Returns true when it took over.
export function handleSessionEnded() {
  if (!isImpersonating()) return false;
  return restoreAdmin('/admin/impersonate');
}

// Logout while impersonating: end the impersonation, then the admin's login.
// Returns the admin's token for the caller's own /logout call, or null.
export async function endImpersonationForLogout() {
  const state = getImpersonation();
  if (!state?.adminToken) return null;
  try {
    await fetch(`${API_URL}/admin/impersonate/stop`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
  } catch (e) {
    // best effort
  }
  localStorage.removeItem(IMPERSONATION_KEY);
  return state.adminToken;
}
